import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAnnouncements } from '../services/api';

// How often the sidebar re-checks for new announcements
const ANNOUNCE_POLL_MS = 120000;

const ROLE_LABELS = {
  super_admin: 'Διαχειριστής',
  shop_worker: 'Κατάστημα',
  factory_worker: 'Εργοστάσιο',
  driver: 'Οδηγός',
};

export default function Sidebar() {
  const {
    user, logout,
    myShops, myFactories,
    selectedShop, setSelectedShop,
    selectedFactory, setSelectedFactory,
  } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // The three most recent announcements, shown as a preview box
  const [recent, setRecent] = useState([]);

  const loadRecent = useCallback(() => {
    if (!user) return;
    getAnnouncements({ limit: 3 })
      .then((r) => setRecent(r.data))
      .catch(() => setRecent([]));
  }, [user]);

  useEffect(() => { loadRecent(); }, [loadRecent]);
  useEffect(() => {
    const id = setInterval(loadRecent, ANNOUNCE_POLL_MS);
    return () => clearInterval(id);
  }, [loadRecent]);
  // Refresh when leaving the announcements page, so edits show immediately
  useEffect(() => { loadRecent(); }, [location.pathname, loadRecent]);
  // ...and when the announcements page says something changed, since adding
  // one while already on that page doesn't change the route
  useEffect(() => {
    const onChanged = () => loadRecent();
    window.addEventListener('announcements-changed', onChanged);
    return () => window.removeEventListener('announcements-changed', onChanged);
  }, [loadRecent]);

  if (!user) return null;

  const initials = (user.full_name || user.username || '?')
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const isAdmin = user.role === 'super_admin';
  const isShop = user.role === 'shop_worker' || isAdmin;
  const isFactory = user.role === 'factory_worker' || isAdmin;
  // A driver only reads: one board, no switchers, no admin section.
  const isDriver = user.role === 'driver';

  const link = (to, label, icon) => (
    <NavLink
      to={to}
      end
      className={({ isActive }) => 'sidebar-link' + (isActive ? ' active' : '')}
      onClick={() => setOpen(false)}
    >
      <span>{icon}</span><span>{label}</span>
    </NavLink>
  );

  // Show shop switcher when user has 2+ shops OR is super_admin
  const showShopSwitcher = isShop && (myShops.length >= 2 || isAdmin);
  // Show factory switcher when user has 2+ factories OR is super_admin
  const showFactorySwitcher = isFactory && (myFactories.length >= 2 || isAdmin);

  const handleShopChange = (e) => {
    const id = Number(e.target.value);
    const s = myShops.find((x) => x.id === id);
    setSelectedShop(s || null);
  };
  const handleFactoryChange = (e) => {
    const id = Number(e.target.value);
    const f = myFactories.find((x) => x.id === id);
    setSelectedFactory(f || null);
  };

  const switcherStyle = {
    width: '100%',
    padding: '8px 10px',
    borderRadius: 6,
    border: '1px solid var(--gray-700)',
    background: 'var(--gray-800)',
    color: 'white',
    fontSize: 13,
    cursor: 'pointer',
  };

  return (
    <>
      <button className="mobile-menu-toggle" onClick={() => setOpen((o) => !o)} aria-label="Menu">
        ☰
      </button>
      <aside className={'sidebar' + (open ? ' open' : '')}>
        <div className="sidebar-header">
          <div className="sidebar-title">📬 Σύστημα Παραγγελιών</div>
          <div className="sidebar-subtitle">Κρητικός Φούρνος</div>
        </div>

        {showShopSwitcher && (
          <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--gray-800)' }}>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Κατάστημα</div>
            <select value={selectedShop?.id || ''} onChange={handleShopChange} style={switcherStyle}>
              {myShops.length === 0 && <option value="">— χωρίς πρόσβαση —</option>}
              {myShops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}

        {showFactorySwitcher && (
          <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--gray-800)' }}>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Εργοστάσιο</div>
            <select value={selectedFactory?.id || ''} onChange={handleFactoryChange} style={switcherStyle}>
              {myFactories.length === 0 && <option value="">— χωρίς πρόσβαση —</option>}
              {myFactories.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
        )}

        <div className="sidebar-section">
          <div className="sidebar-section-title">Κύριο μενού</div>
          {link('/', 'Αρχική', '🏠')}
          {isShop && link('/new-order', 'Νέα Παραγγελία', '🛒')}
          {isShop && link('/my-orders', 'Οι Παραγγελίες μου', '📋')}
          {isFactory && link('/factory-queue', 'Παραγγελίες Εργοστασίου', '🏭')}
          {isDriver && link('/driver-orders', 'Παραγγελίες', '🚚')}
        </div>

        {isAdmin && (
          <div className="sidebar-section">
            <div className="sidebar-section-title">Διαχείριση</div>
            {link('/admin/shops', 'Καταστήματα', '🏪')}
            {link('/admin/factories', 'Εργοστάσια', '🏭')}
            {link('/admin/products', 'Προϊόντα', '🥐')}
            {link('/admin/workers', 'Εργαζόμενοι', '🧑‍🔧')}
            {link('/admin/users', 'Χρήστες', '👥')}
            {link('/admin/export', 'Εξαγωγή', '📤')}
          </div>
        )}

        {/* Announcements: the menu entry plus a preview of the latest few.
            Clicking anything here opens the full list in the main area. */}
        <div className="sidebar-section">
          <div className="sidebar-section-title">Ανακοινώσεις</div>
          {link('/announcements', 'Ανακοινώσεις', '📢')}

          <div style={{ padding: '4px 20px 0' }}>
            {recent.length === 0 ? (
              <div style={{
                fontSize: 11, color: 'var(--gray-500)',
                background: 'var(--gray-800)', borderRadius: 6,
                padding: '8px 10px', textAlign: 'center',
              }}>
                Καμία ανακοίνωση
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {recent.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => { navigate('/announcements'); setOpen(false); }}
                    title={a.body}
                    style={{
                      textAlign: 'left',
                      background: 'var(--gray-800)',
                      border: 'none',
                      borderLeft: `3px solid ${a.is_pinned ? '#f59e0b' : 'var(--gray-700)'}`,
                      borderRadius: 6,
                      padding: '7px 10px',
                      cursor: 'pointer',
                      color: 'var(--gray-300)',
                      width: '100%',
                      display: 'block',
                    }}
                  >
                    <div style={{
                      fontSize: 12, fontWeight: 600, color: 'white',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {a.is_pinned ? '📌 ' : ''}{a.title}
                    </div>
                    <div style={{
                      fontSize: 11, color: 'var(--gray-500)', marginTop: 2,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {a.body}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="sidebar-section">
          <div className="sidebar-section-title">Επικοινωνία</div>
          {link('/contacts', 'Στοιχεία Επικοινωνίας', '📇')}
        </div>

        <div className="sidebar-footer">
          <div className="user-avatar">{initials}</div>
          <div className="user-info">
            <div className="user-name">{user.full_name || user.username}</div>
            <div className="user-role">{ROLE_LABELS[user.role] || user.role}</div>
          </div>
          <button className="logout-btn" onClick={logout} title="Αποσύνδεση">⎋</button>
        </div>
      </aside>
    </>
  );
}
